import { useEffect, useRef } from 'react';

import { cn } from '@ibas/ui';

import { ScrollReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

export interface WaveDividerProps {
  className?: string;
  /** Small uppercase label above the pull-quote. */
  label?: string;
  /** The pull-quote line, rendered to the right of the wave. */
  quote?: string;
}

/** viewBox geometry — a wide, shallow strip the wave lives inside. */
const VIEW_W = 1000;
const VIEW_H = 160;
const MIDLINE = VIEW_H / 2;
/** Anchor points the curve is smoothed through. */
const POINT_COUNT = 9;
const STEP = VIEW_W / (POINT_COUNT - 1);
/** Max vertical deform toward the cursor (viewBox units). */
const MAX_PULL = 46;
/** Horizontal reach of the pointer's influence (viewBox units). */
const INFLUENCE_RADIUS = 320;
/** Per-frame lerp factor — lower = softer spring-back. */
const LERP = 0.12;
/** Below this delta the point is considered settled and the loop can stop. */
const SETTLE_EPSILON = 0.05;

interface WavePoint {
  x: number;
  base: number;
  y: number;
  target: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function createPoints(): WavePoint[] {
  return Array.from({ length: POINT_COUNT }, (_, i) => ({
    x: i * STEP,
    base: MIDLINE,
    y: MIDLINE,
    target: MIDLINE,
  }));
}

/** Smooth quadratic curve threaded through the anchor points. */
function buildPath(points: WavePoint[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M0,${points[0].y} L${VIEW_W},${points[0].y}`;

  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    d += ` Q${points[i].x},${points[i].y} ${midX},${midY}`;
  }
  const last = points[points.length - 1];
  d += ` L${last.x},${last.y}`;
  return d;
}

/**
 * WaveDivider — a living, pointer-reactive SVG section divider paired with the
 * IBAS pull-quote.
 *
 * Motion intent: a thin gold line reads as calm and flat until the cursor
 * approaches — then it deforms toward the pointer (a smooth quadratic curve
 * threaded through a row of anchor points) and eases back to rest via a
 * per-frame lerp once the cursor moves on, a small "the site is alive and
 * paying attention" moment between sections. Under `prefers-reduced-motion:
 * reduce` (and on devices with no hover, e.g. touch) the wave renders as a
 * flat static line with no pointer listeners and no animation loop — the
 * pull-quote is always fully legible either way.
 */
export function WaveDivider({
  className,
  label = 'The IBAS difference',
  quote = 'Flexible by design. Applied in depth. Supportive throughout.',
}: WaveDividerProps) {
  const reduced = useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const pointsRef = useRef<WavePoint[]>(createPoints());
  const rafRef = useRef<number | null>(null);

  // Paint the initial (flat) curve once, regardless of motion preference.
  useEffect(() => {
    pathRef.current?.setAttribute('d', buildPath(pointsRef.current));
  }, []);

  // Pointer deformation + spring-back loop. Skipped entirely under reduced
  // motion, and on devices with no persistent hover pointer.
  useEffect(() => {
    if (reduced) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) return;

    const svg = svgRef.current;
    const path = pathRef.current;
    if (!svg || !path) return;

    const toViewBox = (clientX: number, clientY: number) => {
      const rect = svg.getBoundingClientRect();
      return {
        x: rect.width ? ((clientX - rect.left) / rect.width) * VIEW_W : 0,
        y: rect.height ? ((clientY - rect.top) / rect.height) * VIEW_H : MIDLINE,
      };
    };

    const startLoop = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    const tick = () => {
      let unsettled = false;
      for (const p of pointsRef.current) {
        p.y += (p.target - p.y) * LERP;
        if (Math.abs(p.target - p.y) > SETTLE_EPSILON) unsettled = true;
      }
      path.setAttribute('d', buildPath(pointsRef.current));
      rafRef.current = unsettled ? requestAnimationFrame(tick) : null;
    };

    const handlePointerMove = (e: PointerEvent) => {
      const { x: px, y: py } = toViewBox(e.clientX, e.clientY);
      const dy = clamp(py - MIDLINE, -MAX_PULL, MAX_PULL);
      for (const p of pointsRef.current) {
        const influence = Math.max(0, 1 - Math.abs(p.x - px) / INFLUENCE_RADIUS);
        p.target = p.base + dy * influence;
      }
      startLoop();
    };

    const handlePointerLeave = () => {
      for (const p of pointsRef.current) p.target = p.base;
      startLoop();
    };

    svg.addEventListener('pointermove', handlePointerMove);
    svg.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      svg.removeEventListener('pointermove', handlePointerMove);
      svg.removeEventListener('pointerleave', handlePointerLeave);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [reduced]);

  return (
    <section aria-label="The IBAS difference" className={cn('bg-background', className)}>
      <ScrollReveal
        as="div"
        className="mx-auto grid max-w-6xl items-center gap-8 px-6 py-14 sm:py-20 lg:grid-cols-[1.4fr_1fr] lg:gap-16"
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          className="h-24 w-full text-brand-gold sm:h-32"
          role="presentation"
          aria-hidden="true"
        >
          <path
            ref={pathRef}
            className="fill-none stroke-current"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </svg>

        <div className="lg:border-l lg:border-border lg:pl-10">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">{label}</p>
          <p className="mt-3 font-display text-2xl leading-snug font-medium text-foreground sm:text-3xl">
            {quote}
          </p>
        </div>
      </ScrollReveal>
    </section>
  );
}
